import { useState } from "react";
import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  fromCanonicalKg,
  toCanonicalKg,
  type UnitsPreference,
} from "@jackedlogs/shared";

import { Button } from "../primitives/Button";
import { TextField } from "../primitives/TextField";
import { makeQuickEntryCardStyles } from "./glanceCardStyles";
import type { UiTheme } from "../theme";

// Home quick weigh-in. The most-changed body value reachable in ~one tap
// (home-redesign.md: Home owns the quick-add; Settings -> Measurements does
// NOT duplicate it). Lifted into @jackedlogs/ui so mobile + the web-rnw
// console render one card. The write itself is a host seam (onSave): mobile
// goes through useMeasurements (outbox + optimistic overlay) then refreshes
// the profile; web POSTs /api/me/measurements. `queued` lets the offline
// mobile host surface its outbox depth; web passes 0.
const numOrUndef = (s: string): number | undefined => {
  const v = parseFloat(s.replace(",", "."));
  return Number.isFinite(v) && v > 0 ? v : undefined;
};

export function QuickWeightCard({
  theme,
  units,
  bodyweightKg,
  queued,
  onSave,
}: {
  theme: UiTheme;
  units: UnitsPreference;
  /** Canonical (kg) current bodyweight, or null when unknown. */
  bodyweightKg: number | null;
  /** Outbox depth (mobile); web passes 0. */
  queued: number;
  /** Persist a canonical-kg bodyweight; returns true on success. */
  onSave: (canonicalKg: number) => Promise<boolean>;
}) {
  const { t, i18n } = useTranslation();
  const styles = makeQuickEntryCardStyles(theme);

  const current =
    typeof bodyweightKg === "number"
      ? fromCanonicalKg(bodyweightKg, units)
      : null;

  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const submit = async () => {
    const parsed = numOrUndef(value);
    if (parsed === undefined || saving) return;
    setSaving(true);
    setJustSaved(false);
    try {
      const ok = await onSave(toCanonicalKg(parsed, units));
      if (ok) {
        setValue("");
        setJustSaved(true);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={styles.card}>
      <View style={styles.textCol}>
        <Text style={styles.label}>
          {t("mobile.home.weightTitle", { defaultValue: "Body weight" })}
        </Text>
        <Text
          style={[
            styles.hint,
            current !== null && !justSaved && styles.hintValue,
          ]}
        >
          {justSaved
            ? t("mobile.home.weightSaved", { defaultValue: "Saved ✓" })
            : current !== null
              ? t("mobile.home.weightCurrent", {
                  defaultValue: "Latest: {{value}} {{unit}}",
                  value: new Intl.NumberFormat(i18n.language).format(current),
                  unit: units,
                })
              : t("mobile.home.weightNone", {
                  defaultValue: "Log your weight to track trends.",
                })}
        </Text>
      </View>
      <TextField
        theme={theme}
        numeric
        style={styles.input}
        value={value}
        onChangeText={setValue}
        placeholder={current !== null ? String(current) : units}
        accessibilityLabel={t("mobile.home.weightTitle", {
          defaultValue: "Body weight",
        })}
        returnKeyType="done"
        onSubmitEditing={() => void submit()}
      />
      <Button
        theme={theme}
        label={t("mobile.home.weightSave", { defaultValue: "Save" })}
        // Two "Save" buttons sit on this screen — this one and the glucose
        // card's — and a screen reader announced both as bare "Save", with
        // only position to tell them apart (DS-47, seen in the QA bench's
        // accessibility map). The visible label stays short; the announced one
        // says what it saves.
        accessibilityLabel={t("mobile.home.weightSaveA11y", {
          defaultValue: "Save body weight",
        })}
        onPress={() => void submit()}
        loading={saving}
        disabled={numOrUndef(value) === undefined}
        style={styles.saveBtn}
      />
      {queued > 0 ? (
        <Text style={styles.queued}>
          {t("mobile.body.queuedNote", {
            defaultValue: "Waiting to sync: {{count}}",
            count: queued,
          })}
        </Text>
      ) : null}
    </View>
  );
}

